import { useEffect, useState } from 'react'
import { useOrganizador } from '../../contexts/OrganizadorContext'
import { Globe, ExternalLink, Copy, Check, Eye, MessageCircle, TrendingUp, BarChart3 } from 'lucide-react'
import DateRangePicker from '../../components/ui/DateRangePicker'
import api from '../../services/api'

export default function CatalogoOnlineRedirect() {
  const { organizadorAtivo } = useOrganizador()
  const [copied, setCopied] = useState(false)
  const [analytics, setAnalytics] = useState(null)
  const [loadingAnalytics, setLoadingAnalytics] = useState(false)
  const [filtros, setFiltros] = useState(() => {
    const now = new Date()
    const start = new Date(now.getFullYear(), now.getMonth(), 1)
    return { data_inicio: start.toISOString().slice(0, 10), data_fim: now.toISOString().slice(0, 10) }
  })

  const slug = organizadorAtivo?.link_catalogo
  const orgId = organizadorAtivo?.id
  const catalogoUrl = slug ? `${window.location.origin}/catalogo/${slug}` : null

  const handleCopy = () => {
    if (catalogoUrl) {
      navigator.clipboard.writeText(catalogoUrl)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    }
  }

  const carregarAnalytics = async () => {
    if (!orgId) return
    setLoadingAnalytics(true)
    try {
      const params = new URLSearchParams()
      if (filtros.data_inicio) params.append('data_inicio', filtros.data_inicio)
      if (filtros.data_fim) params.append('data_fim', filtros.data_fim)
      const res = await api.get(`/api/catalogo-online/analytics/${orgId}?${params}`)
      setAnalytics(res.data)
    } catch {
      setAnalytics(null)
    } finally {
      setLoadingAnalytics(false)
    }
  }

  useEffect(() => {
    if (orgId && slug) carregarAnalytics()
  }, [orgId, slug, filtros])

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-teal-600 to-cyan-700 rounded-xl px-4 sm:px-6 py-4 sm:py-6">
        <div className="flex items-center gap-3">
          <Globe className="w-7 h-7 text-white" />
          <div>
            <h1 className="text-xl sm:text-3xl font-bold text-white">Catálogo Online</h1>
            <p className="text-teal-100 mt-1 text-sm">Página pública do catálogo do organizador</p>
          </div>
        </div>
      </div>

      <div className="bg-white shadow-sm rounded-xl border border-gray-100 p-6">
        {slug ? (
          <div className="space-y-6">
            <div>
              <h3 className="text-lg font-medium text-gray-900 mb-2">Link do Catálogo Online</h3>
              <p className="text-sm text-gray-600 mb-4">
                Este é o link público do catálogo online do organizador. Compartilhe com compradores e interessados.
              </p>
            </div>

            {/* URL Display */}
            <div className="flex items-center gap-3 p-4 bg-gray-50 rounded-lg border border-gray-200">
              <Globe className="w-5 h-5 text-teal-600 flex-shrink-0" />
              <span className="text-sm font-medium text-blue-600 break-all flex-1">
                {catalogoUrl}
              </span>
              <button
                onClick={handleCopy}
                className="flex items-center gap-1 px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors flex-shrink-0"
              >
                {copied ? <Check className="w-3.5 h-3.5 text-green-600" /> : <Copy className="w-3.5 h-3.5" />}
                {copied ? 'Copiado!' : 'Copiar'}
              </button>
            </div>

            {/* Actions */}
            <div className="flex flex-wrap gap-3">
              <a
                href={`/catalogo/${slug}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-2 px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700 transition-colors text-sm font-medium"
              >
                <ExternalLink className="w-4 h-4" />
                Abrir Catálogo
              </a>
              <a
                href="/configuracoes/catalogo-online"
                className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-200 transition-colors text-sm font-medium"
              >
                Configurações do Catálogo
              </a>
            </div>
          </div>
        ) : (
          <div className="text-center py-8">
            <Globe className="w-12 h-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">Slug não configurado</h3>
            <p className="text-sm text-gray-600 mb-4">
              Para usar o catálogo online, configure o slug na aba "Integrações" do organizador.
            </p>
            <a
              href={`/organizadores/${organizadorAtivo?.id}/detalhes`}
              className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium"
            >
              Ir para Organizador
            </a>
          </div>
        )}
      </div>

      {/* Métricas */}
      {slug && (
        <div className="space-y-4">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <h2 className="text-lg font-bold text-gray-900 flex items-center gap-2">
              <BarChart3 className="w-5 h-5 text-teal-600" />
              Métricas do Catálogo
            </h2>
            <DateRangePicker
              startDate={filtros.data_inicio}
              endDate={filtros.data_fim}
              onChange={({ data_inicio, data_fim }) => setFiltros({ data_inicio, data_fim })}
              className="w-64"
              align="right"
            />
          </div>

          {/* Cards de métricas */}
          {loadingAnalytics ? (
            <div className="bg-white rounded-xl border border-gray-200 p-8 text-center text-gray-400">
              Carregando métricas...
            </div>
          ) : analytics ? (
            <>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="p-2 bg-blue-100 rounded-lg">
                      <Eye className="w-5 h-5 text-blue-600" />
                    </div>
                    <span className="text-sm font-medium text-gray-500">Acessos</span>
                  </div>
                  <p className="text-3xl font-bold text-gray-900">{analytics.total_acessos}</p>
                </div>
                <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="p-2 bg-green-100 rounded-lg">
                      <MessageCircle className="w-5 h-5 text-green-600" />
                    </div>
                    <span className="text-sm font-medium text-gray-500">Tenho Interesse</span>
                  </div>
                  <p className="text-3xl font-bold text-gray-900">{analytics.total_interesses}</p>
                </div>
                <div className="bg-white rounded-xl border border-gray-100 shadow-sm p-5">
                  <div className="flex items-center gap-3 mb-2">
                    <div className="p-2 bg-purple-100 rounded-lg">
                      <TrendingUp className="w-5 h-5 text-purple-600" />
                    </div>
                    <span className="text-sm font-medium text-gray-500">Taxa de Conversão</span>
                  </div>
                  <p className="text-3xl font-bold text-gray-900">{analytics.taxa_conversao}%</p>
                </div>
              </div>

              {/* Top lotes + Últimos interesses */}
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {/* Top lotes */}
                {analytics.top_lotes?.length > 0 && (
                  <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                    <div className="px-5 py-3 border-b border-gray-100 bg-gray-50">
                      <h3 className="text-sm font-semibold text-gray-700">🔥 Lotes mais procurados</h3>
                    </div>
                    <div className="divide-y divide-gray-50">
                      {analytics.top_lotes.map((item, idx) => (
                        <div key={idx} className="px-5 py-2.5 flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <span className="text-xs font-bold text-gray-400 w-5">{idx + 1}.</span>
                            <span className="font-medium text-gray-900">Lote {item.numero_lote}</span>
                          </div>
                          <span className="px-2 py-0.5 bg-green-100 text-green-700 text-xs font-bold rounded-full">
                            {item.total} clique{item.total !== 1 ? 's' : ''}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Últimos interesses */}
                {analytics.ultimos_interesses?.length > 0 && (
                  <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
                    <div className="px-5 py-3 border-b border-gray-100 bg-gray-50">
                      <h3 className="text-sm font-semibold text-gray-700">📋 Últimos interesses</h3>
                    </div>
                    <div className="divide-y divide-gray-50 max-h-64 overflow-y-auto">
                      {analytics.ultimos_interesses.map((item, idx) => (
                        <div key={idx} className="px-5 py-2.5 flex items-center justify-between">
                          <div>
                            <span className="font-medium text-gray-900 text-sm">Lote {item.numero_lote}</span>
                            {item.contato_nome && (
                              <span className="text-xs text-gray-400 ml-2">→ {item.contato_nome}</span>
                            )}
                          </div>
                          <span className="text-xs text-gray-400">
                            {item.created_at ? new Date(item.created_at).toLocaleString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' }) : ''}
                          </span>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {/* Gráfico de acessos por dia (simples, barras) */}
              {analytics.acessos_por_dia?.length > 0 && (
                <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-5">
                  <h3 className="text-sm font-semibold text-gray-700 mb-4">📊 Acessos por dia</h3>
                  <div className="flex items-end gap-1 h-32">
                    {(() => {
                      const max = Math.max(...analytics.acessos_por_dia.map(d => d.total), 1)
                      return analytics.acessos_por_dia.map((d, idx) => (
                        <div key={idx} className="flex-1 flex flex-col items-center gap-1" title={`${d.dia}: ${d.total} acessos`}>
                          <span className="text-[10px] text-gray-400">{d.total}</span>
                          <div
                            className="w-full bg-teal-500 rounded-t-sm min-h-[2px] transition-all"
                            style={{ height: `${(d.total / max) * 100}%` }}
                          />
                          <span className="text-[9px] text-gray-400 truncate w-full text-center">
                            {new Date(d.dia).toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit' })}
                          </span>
                        </div>
                      ))
                    })()}
                  </div>
                </div>
              )}
            </>
          ) : null}
        </div>
      )}
    </div>
  )
}
